/* eslint-disable no-underscore-dangle */
const SCRIPT_ID = 'super-phoebe-widget-script';
const ROOT_ID = 'phoebe-chat-root';
const SCRIPT_TIMEOUT_MS = 15000;
// The vendor's pre-mount flag requests each have a 500 ms timeout. Keep a
// bounded cancellation guard for an already executing, async self-mount.
const MOUNT_SETTLE_MS = 5000;
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', 'localhost.qaols.phoenix.edu']);

let activeWidget = null;
let lastOwnedConfig = null;

const requireHttpsUrl = value => {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error('Super Phoebe widget URLs are required.');
  }
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.username || url.password) {
    throw new Error('Super Phoebe widget URLs must use HTTPS without embedded credentials.');
  }
  return url.href;
};

const isPhoebeBundleScript = script => {
  if (script.id === SCRIPT_ID) return true;
  if (!script.src) return false;
  try {
    return new URL(script.src, document.baseURI).pathname.endsWith('/phoebe-chat.global.js');
  } catch {
    return false;
  }
};

const removeVendorRoot = widget => {
  if (window.__PHOEBE_CONFIG__ !== widget.config) return;
  const { root, api } = widget;
  if (!root && !api) return;
  try {
    api?.unmount();
  } finally {
    root?.remove();
    if (api && window.__PHOEBE_CHAT_ROOT__ === api) delete window.__PHOEBE_CHAT_ROOT__;
    Object.assign(widget, { root: null, api: null });
  }
};

const stopObserver = widget => {
  widget.observer.disconnect();
  window.clearTimeout(widget.settleTimeout);
};

const capturePendingRoot = widget => {
  const hasPendingRoot = widget.observer.takeRecords().some(({ addedNodes }) => (
    Array.from(addedNodes).some(node => (
      node.nodeType === 1 && (node.id === ROOT_ID || node.querySelector(`#${ROOT_ID}`))
    ))
  ));
  if (!hasPendingRoot || window.__PHOEBE_CONFIG__ !== widget.config) return;
  Object.assign(widget, {
    root: document.getElementById(ROOT_ID),
    api: window.__PHOEBE_CHAT_ROOT__,
  });
};

const stopWidget = widget => {
  if (widget.stopped) return;
  Object.assign(widget, { stopped: true });
  if (activeWidget === widget) activeWidget = null;
  Object.assign(widget.config, { enabled: false });
  if (window.__PHOEBE_CONFIG__ === widget.config) lastOwnedConfig = widget.config;
  window.clearTimeout(widget.loadTimeout);
  widget.script.removeEventListener('load', widget.onLoad);
  widget.script.removeEventListener('error', widget.onFailure);
  widget.script.remove();
  capturePendingRoot(widget);
  removeVendorRoot(widget);
  // A script already executing may finish its async mount after removal.
  // Its config is fenced off, and the observer removes a late owned root.
  Object.assign(widget, {
    settleTimeout: window.setTimeout(() => stopObserver(widget), MOUNT_SETTLE_MS),
  });
};

const releaseWidget = widget => {
  let released = false;
  return () => {
    if (released || widget.stopped) return;
    released = true;
    Object.assign(widget, { consumers: widget.consumers - 1 });
    if (widget.consumers === 0) stopWidget(widget);
  };
};

const mountSuperPhoebeWidget = ({
  scriptUrl, backendUrl, localOverride = false, onError = () => {}, visitorContextProvider,
}) => {
  const source = requireHttpsUrl(scriptUrl);
  const backend = requireHttpsUrl(backendUrl).replace(/\/$/, '');
  if (activeWidget) {
    if (activeWidget.source !== source || activeWidget.backend !== backend) {
      throw new Error('A different Super Phoebe widget configuration is already active.');
    }
    activeWidget.consumers += 1;
    activeWidget.errorCallbacks.add(onError);
    return releaseWidget(activeWidget);
  }
  // Respect a widget installed by another host integration.
  if (document.getElementById(ROOT_ID)) return () => {};
  // A host integration may have inserted the self-mounting bundle before its root exists.
  // Preserve its config and avoid executing another copy, even if it chose a different script ID.
  if (Array.from(document.scripts).some(isPhoebeBundleScript)) return () => {};
  if (window.__PHOEBE_CONFIG__ && window.__PHOEBE_CONFIG__ !== lastOwnedConfig) return () => {};

  const config = { backendUrl: backend, enableGuestBootstrap: true };
  if (typeof visitorContextProvider === 'function') config.visitorContextProvider = visitorContextProvider;
  if (localOverride && LOCAL_HOSTS.has(window.location.hostname)) config.enabled = true;
  window.__PHOEBE_CONFIG__ = config;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = source;
  script.async = true;

  const widget = {
    source,
    backend,
    config,
    script,
    consumers: 1,
    stopped: false,
    root: null,
    api: null,
    errorCallbacks: new Set([onError]),
  };
  widget.observer = new MutationObserver(() => {
    const root = document.getElementById(ROOT_ID);
    if (!root) return;
    if (!widget.stopped && activeWidget === widget && window.__PHOEBE_CONFIG__ !== widget.config) {
      stopWidget(widget);
    } else if (!widget.stopped && activeWidget === widget) {
      widget.root = root;
      widget.api = window.__PHOEBE_CHAT_ROOT__;
      stopObserver(widget);
    } else if (!activeWidget && window.__PHOEBE_CONFIG__ === widget.config) {
      widget.root = root;
      widget.api = window.__PHOEBE_CHAT_ROOT__;
      removeVendorRoot(widget);
      stopObserver(widget);
    }
  });
  widget.onLoad = () => {
    window.clearTimeout(widget.loadTimeout);
  };
  widget.onFailure = () => {
    stopWidget(widget);
    widget.errorCallbacks.forEach(callback => callback(new Error('Super Phoebe bundle could not be loaded.')));
  };
  widget.observer.observe(document.body, { childList: true, subtree: true });
  script.addEventListener('load', widget.onLoad);
  script.addEventListener('error', widget.onFailure);
  widget.loadTimeout = window.setTimeout(widget.onFailure, SCRIPT_TIMEOUT_MS);
  activeWidget = widget;
  document.head.appendChild(script);
  return releaseWidget(widget);
};

export default mountSuperPhoebeWidget;
